import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Input } from 'antd';
import DrawerModal from '../../../../components/shared/DrawerModal';
import styles from './PurposeStatusModal.styles';
import {
  getIsVisibleModal,
  closeModal,
} from '../../../../redux/store/ui/common';
import { useIntl } from 'react-intl';
import SelectorLayout from '../../../shared/SelectorLayout';
import {
  createPurposes,
  fetchActivationEventsIfNeeded,
  fetchGroupsIfNeeded,
  selectActivationEvents,
  selectFieldGroups,
  selectCollectionNames,
  selectDisplayPrivacyPolicy,
  selectSelectedPrivacyPolicy,
  selectSelectedPurpose,
  updatePurposes,
  fetchCollectionNamesIfNeeded,
} from '../../../../redux/store/appData/privacyPolicy';
import {
  ProcessingPurposeInput,
  ProcessingPurposeData,
  RetentionOverrideInput,
} from '../../../../@types';
import {
  formatToISO8601Duration,
  getLocalizedDurationLabel,
  getPurposeBaseName,
  getUsersPurpose,
  parseISO8601Duration,
  serialiseOverrides,
  USERS_COLLECTION,
} from '../../../../utils';
import { RetentionOverrideRowState } from '../../types';
import RetentionOverrideRow from './RetentionOverrideRow';
import { optionsForBlockReason } from '../../../Filter/assets/optionsForUsers';

// Whether the backend accepts `retentionOverrides: []` as "clear the list".
// The PUT body is a partial merge and this endpoint already ignores '' and null
// for min/maxDuration; an empty array is non-null so it should pass a plain
// null-guard. VERIFY ON STAGING — if overrides come back after unchecking,
// flip this to false and the checkbox becomes one-way once overrides exist.
const OVERRIDES_CLEARABLE = true;

// What a cleared min/maxDuration is sent as on edit. Neither '' nor null
// cleared the value on the backend in earlier attempts; the overrides-only
// use case (parent max empty) needs it retried — flip to '' if null fails.
const CLEARED_DURATION: null | '' = null;

const USER_STATUS_OPTIONS = ['active', 'blocked'];

const durationUnits = [
  { label: 'Days', value: 'days' },
  // { label: 'Weeks', value: 'weeks' },
  { label: 'Months', value: 'months' },
  { label: 'Years', value: 'years' },
  { label: 'Hours', value: 'hours' },
  { label: 'Minutes', value: 'minutes' },
  { label: 'Seconds', value: 'seconds' },
];

const PurposeStatusModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedPurpose = useSelector(selectSelectedPurpose);
  const selectedPolicy = useSelector(selectSelectedPrivacyPolicy);
  const displayPolicies = useSelector(selectDisplayPrivacyPolicy);
  const collectionNames = useSelector(selectCollectionNames);
  const activationEvents = useSelector(selectActivationEvents);
  const groups = useSelector(selectFieldGroups);
  const visibleModal = useSelector(getIsVisibleModal('PurposeStatusModal'));

  // Find the parent policy: use selectedPolicy for create mode, or find from displayPolicies for edit mode
  const parentPolicy = useMemo(() => {
    if (selectedPurpose) {
      // Edit mode - find policy containing this purpose
      return displayPolicies?.find((policy) =>
        policy?.processingPurposes?.some((p) => p.id === selectedPurpose?.id)
      );
    }
    // Create mode - use the currently selected policy
    return selectedPolicy;
  }, [displayPolicies, selectedPurpose?.id, selectedPolicy]);

  // Compute grouped purpose data (all related purposes with same base name)
  const groupedPurposeData = useMemo(() => {
    if (!selectedPurpose) return null;

    const baseName = getPurposeBaseName(selectedPurpose?.name || '');
    const allPurposes = parentPolicy?.processingPurposes || [];

    // Find all purposes with the same base name and activation event
    const relatedPurposes = allPurposes.filter(
      (p: ProcessingPurposeData) =>
        getPurposeBaseName(p.name || '') === baseName &&
        p.activationEventKey === selectedPurpose?.activationEventKey
    );

    const purposes =
      relatedPurposes.length > 0 ? relatedPurposes : [selectedPurpose];

    // Merge collection names and field groups from all purposes
    const mergedCollectionNames = [
      ...new Set(
        purposes
          .map((p: ProcessingPurposeData) => p.collectionName)
          .filter((c): c is string => !!c && c.length > 0)
      ),
    ];
    const mergedFieldGroupKeys = [
      ...new Set(
        purposes
          .flatMap((p: ProcessingPurposeData) => p.fieldGroupKeys || [])
          .filter((g) => !!g)
      ),
    ];

    return {
      baseName,
      purposes,
      collectionNames: mergedCollectionNames,
      fieldGroupKeys: mergedFieldGroupKeys,
      activationEventKey: selectedPurpose?.activationEventKey || '',
      minDuration: selectedPurpose?.minDuration || '',
      maxDuration: selectedPurpose?.maxDuration || '',
      legalBasis: selectedPurpose?.legalBasis || '',
    };
  }, [selectedPurpose, parentPolicy]);

  const [selectedName, setSelectedName] = useState('');
  const [selectedCollectionNames, setSelectedCollectionNames] = useState<any>(
    []
  );
  const [selectedStartDate, setSelectedStartDate] = useState<any>('');
  const [selectedMinDurationNumber, setSelectedMinDurationNumber] =
    useState('');
  const [selectedMinDurationUnit, setSelectedMinDurationUnit] =
    useState('days');
  const [selectedMaxDurationNumber, setSelectedMaxDurationNumber] =
    useState('');
  const [selectedMaxDurationUnit, setSelectedMaxDurationUnit] =
    useState('days');

  const [selectedGroups, setSelectedGroups] = useState<any>([]);
  const [selectedLegalBasis, setSelectedLegalBasis] = useState('');
  const [overridesEnabled, setOverridesEnabled] = useState(false);
  const [overrideRows, setOverrideRows] = useState<RetentionOverrideRowState[]>(
    []
  );
  // Rows are removable, so keys must be stable rather than positional.
  const overrideKeySeq = useRef(0);
  const nextOverrideKey = () => `override-${overrideKeySeq.current++}`;

  const onChangeName = (e) => setSelectedName(e.target.value);

  const onChangeMinDurationNumber = (e) => {
    if (e.target.value >= 0) setSelectedMinDurationNumber(e.target.value);
  };

  const onChangeMinDurationUnit = (value) => setSelectedMinDurationUnit(value);

  const onChangeMaxDurationNumber = (e) => {
    if (e.target.value >= 0) setSelectedMaxDurationNumber(e.target.value);
  };

  const onChangeMaxDurationUnit = (value) => setSelectedMaxDurationUnit(value);

  const isEditMode = !!groupedPurposeData;

  // Overrides are keyed by user block status, so they only apply to the users
  // data collection. Deselecting it hides the section without discarding rows.
  const overridesApplicable =
    selectedCollectionNames?.includes(USERS_COLLECTION);
  const overridesActive = overridesApplicable && overridesEnabled;

  // Overrides refine maxDuration only. While they are on, min is forced empty
  // and max is optional — a purpose may exist purely through its overrides.
  // Otherwise: on edit a purpose keeps its duration type — min stays min, max
  // stays max (the backend does not clear the other value), so the unused
  // side is locked; on create, filling one side locks the other.
  const minDurationDisabled = overridesActive
    ? true
    : isEditMode
    ? !groupedPurposeData.minDuration
    : !selectedMinDurationNumber && !!selectedMaxDurationNumber;
  const maxDurationDisabled = overridesActive
    ? false
    : isEditMode
    ? !groupedPurposeData.maxDuration
    : !selectedMaxDurationNumber && !!selectedMinDurationNumber;
  // Overrides on: min must be empty, max free. Otherwise edit: the populated
  // sides must match the original; create: exactly one.
  const durationsValid = overridesActive
    ? !selectedMinDurationNumber
    : isEditMode
    ? !!selectedMinDurationNumber === !!groupedPurposeData.minDuration &&
      !!selectedMaxDurationNumber === !!groupedPurposeData.maxDuration
    : !!selectedMinDurationNumber !== !!selectedMaxDurationNumber;

  const statusOptions = useMemo(
    () =>
      USER_STATUS_OPTIONS.map((option) => ({
        label: intl.formatMessage({ id: `fleetadmin.status.${option}` }),
        value: option,
      })),
    [intl]
  );
  const blockReasonOptions = useMemo(
    () =>
      optionsForBlockReason.map((option) => ({
        label: intl.formatMessage({ id: `fleetadmin.blockReasons.${option}` }),
        value: option,
      })),
    [intl]
  );
  const durationUnitOptions = useMemo(
    () =>
      durationUnits.map((unit) => ({
        label: getLocalizedDurationLabel(unit.value),
        value: unit.value,
      })),
    []
  );

  // '0' is a truthy string, and formatToISO8601Duration would turn it into a
  // meaningless 'P0Y' — require an actual positive number.
  const isRowComplete = (row: RetentionOverrideRowState) =>
    Number(row.durationNumber) > 0 && !!row.durationUnit;

  // Two rows targeting the same audience can never both apply.
  const overrideSignature = (row: RetentionOverrideRowState) =>
    `${row.blocked}|${[...row.blockReasons].sort().join(',')}`;
  const hasDuplicateOverrides =
    new Set(overrideRows.map(overrideSignature)).size !== overrideRows.length;

  const overridesValid =
    !overridesActive ||
    (overrideRows.length > 0 &&
      overrideRows.every(isRowComplete) &&
      !hasDuplicateOverrides);

  const makeEmptyOverrideRow = (): RetentionOverrideRowState => ({
    key: nextOverrideKey(),
    blocked: true,
    blockReasons: [],
    durationNumber: '',
    durationUnit: 'days',
  });

  const onToggleOverrides = (e) => {
    const enabled = e.target.checked;
    setOverridesEnabled(enabled);
    if (enabled) {
      // Overrides are max-only: min is disabled and cleared while they are on.
      setSelectedMinDurationNumber('');
      setSelectedMinDurationUnit('days');
      // Never leave the section checked but blank.
      if (overrideRows.length === 0) {
        setOverrideRows([makeEmptyOverrideRow()]);
      }
    } else if (groupedPurposeData) {
      // Unticking on edit restores the purpose's original min so an
      // accidental tick doesn't force the admin to retype it.
      const { number, unit } = parseISO8601Duration(
        groupedPurposeData.minDuration
      );
      setSelectedMinDurationNumber(number.toString());
      setSelectedMinDurationUnit(unit);
    }
  };

  const onChangeOverrideRow = (
    key: string,
    patch: Partial<RetentionOverrideRowState>
  ) =>
    setOverrideRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );

  const onRemoveOverrideRow = (key: string) =>
    setOverrideRows((prev) => prev.filter((row) => row.key !== key));

  const onAddOverrideRow = () =>
    setOverrideRows((prev) => [...prev, makeEmptyOverrideRow()]);

  // parseISO8601Duration can return 'weeks' (from P2W), which durationUnits
  // does not offer — fold it into days so the unit Select always has a match.
  const toOverrideRowState = (
    override: RetentionOverrideInput
  ): RetentionOverrideRowState => {
    const { number, unit } = parseISO8601Duration(
      override?.maxDuration || override?.minDuration
    );
    const isWeeks = unit === 'weeks';
    return {
      key: nextOverrideKey(),
      blocked: !!override?.blocked,
      blockReasons: override?.blocked
        ? [...(override?.blockReasons || [])]
        : [],
      durationNumber: isWeeks ? String(Number(number) * 7) : number.toString(),
      durationUnit: isWeeks ? 'days' : unit,
    };
  };

  const resetFormState = () => {
    if (groupedPurposeData) {
      // Edit mode - use grouped data
      setSelectedName(groupedPurposeData.baseName);
      setSelectedCollectionNames(groupedPurposeData.collectionNames);
      setSelectedStartDate(groupedPurposeData.activationEventKey);
      setSelectedGroups(groupedPurposeData.fieldGroupKeys);
      setSelectedLegalBasis(groupedPurposeData.legalBasis);

      const { number: minNumber, unit: minUnit } = parseISO8601Duration(
        groupedPurposeData.minDuration
      );
      setSelectedMinDurationNumber(minNumber.toString());
      setSelectedMinDurationUnit(minUnit);

      const { number: maxNumber, unit: maxUnit } = parseISO8601Duration(
        groupedPurposeData.maxDuration
      );
      setSelectedMaxDurationNumber(maxNumber.toString());
      setSelectedMaxDurationUnit(maxUnit);

      // Overrides live only on the users sibling, never on the clicked one.
      const serverOverrides =
        getUsersPurpose(groupedPurposeData.purposes)?.retentionOverrides || [];
      setOverridesEnabled(serverOverrides.length > 0);
      setOverrideRows(serverOverrides.map(toOverrideRowState));
      // Overrides are max-only: with any present, min shows (and submits)
      // as cleared even if the backend still holds a value.
      if (serverOverrides.length > 0) {
        setSelectedMinDurationNumber('');
        setSelectedMinDurationUnit('days');
      }
    } else {
      // Create mode - reset to empty
      setSelectedName('');
      setSelectedCollectionNames([]);
      setSelectedStartDate('');
      setSelectedGroups([]);
      setSelectedLegalBasis('');
      setSelectedMinDurationNumber('');
      setSelectedMinDurationUnit('days');
      setSelectedMaxDurationNumber('');
      setSelectedMaxDurationUnit('days');
      setOverridesEnabled(false);
      setOverrideRows([]);
    }
  };

  const handleOnCancel = (e) => {
    e.preventDefault();
    resetFormState();
    dispatch(closeModal('PurposeStatusModal'));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    // A cleared side is sent as CLEARED_DURATION on edit so the backend can
    // (hopefully) clear it; on create it is simply omitted.
    const minIsoDuration = selectedMinDurationNumber
      ? formatToISO8601Duration(
          selectedMinDurationNumber,
          selectedMinDurationUnit
        )
      : CLEARED_DURATION;
    const maxIsoDuration = selectedMaxDurationNumber
      ? formatToISO8601Duration(
          selectedMaxDurationNumber,
          selectedMaxDurationUnit
        )
      : CLEARED_DURATION;

    // Overrides are max-only; minDuration is never emitted on a rule.
    // Order is preserved: the backend reports the matched rule by index.
    const overridesPayload: RetentionOverrideInput[] = overridesActive
      ? overrideRows.filter(isRowComplete).map((row) => ({
          blocked: row.blocked,
          blockReasons: row.blocked ? row.blockReasons : [],
          maxDuration: formatToISO8601Duration(
            row.durationNumber,
            row.durationUnit
          ),
        }))
      : [];

    if (groupedPurposeData && groupedPurposeData.purposes.length > 0) {
      // Editing existing grouped purpose - update all related purposes
      setIsSubmitting(true);

      const updates = groupedPurposeData.purposes.map(
        (purpose: ProcessingPurposeData) => {
          const payload = {} as ProcessingPurposeInput;

          // Update name with collection suffix if multiple collections
          const newName =
            selectedCollectionNames.length > 1
              ? `${selectedName}::${purpose.collectionName}`
              : selectedName;

          if (
            selectedName !== groupedPurposeData.baseName &&
            purpose.name !== newName
          ) {
            payload.name = newName;
          }
          if (purpose.activationEventKey !== selectedStartDate) {
            payload.activationEventKey = selectedStartDate;
          }
          if ((purpose.minDuration || CLEARED_DURATION) !== minIsoDuration) {
            payload.minDuration = minIsoDuration;
          }
          if ((purpose.maxDuration || CLEARED_DURATION) !== maxIsoDuration) {
            payload.maxDuration = maxIsoDuration;
          }
          // Update field groups for this purpose's collection only
          const fieldGroupsForCollection = selectedGroups.filter((groupKey) => {
            const group = groups?.find((g) => g.key === groupKey);
            return group?.collectionName === purpose.collectionName;
          });
          // Compare order-insensitively on copies — `purpose` comes straight
          // from the store, so sorting it in place would mutate state.
          if (
            JSON.stringify([...(purpose.fieldGroupKeys || [])].sort()) !==
            JSON.stringify([...fieldGroupsForCollection].sort())
          ) {
            payload.fieldGroupKeys = fieldGroupsForCollection;
          }
          if (
            selectedLegalBasis !== '' &&
            purpose.legalBasis !== selectedLegalBasis
          ) {
            payload.legalBasis = selectedLegalBasis;
          }
          // Overrides go on the users sibling only. Other siblings never
          // received any, so there is nothing to clear on them.
          if (
            purpose.collectionName === USERS_COLLECTION &&
            (OVERRIDES_CLEARABLE || overridesPayload.length > 0) &&
            serialiseOverrides(purpose.retentionOverrides) !==
              serialiseOverrides(overridesPayload)
          ) {
            payload.retentionOverrides = overridesPayload;
          }

          return { id: purpose.id, payload };
        }
      );

      // Only send updates that have changes
      const updatesWithChanges = updates.filter(
        (u) => Object.keys(u.payload).length > 0
      );

      if (updatesWithChanges.length > 0) {
        await dispatch(updatePurposes(updatesWithChanges));
      }

      setIsSubmitting(false);
      dispatch(closeModal('PurposeStatusModal'));
    } else {
      // Creating new purposes - one per selected collection
      const payloads = selectedCollectionNames?.map((collectionName) => {
        // Filter field groups to only include those for this collection
        const fieldGroupsForCollection = selectedGroups.filter((groupKey) => {
          const group = groups?.find((g) => g.key === groupKey);
          return group?.collectionName === collectionName;
        });

        // Make name unique per collection using :: delimiter (reversible for grouping later)
        const uniqueName =
          selectedCollectionNames.length > 1
            ? `${selectedName}::${collectionName}`
            : selectedName;

        return {
          name: uniqueName,
          activationEventKey: selectedStartDate,
          ...(selectedMinDurationNumber && { minDuration: minIsoDuration }),
          ...(selectedMaxDurationNumber && { maxDuration: maxIsoDuration }),
          ...(collectionName === USERS_COLLECTION &&
            overridesPayload.length > 0 && {
              retentionOverrides: overridesPayload,
            }),
          fieldGroupKeys: fieldGroupsForCollection,
          legalBasis: selectedLegalBasis,
          policyId: parentPolicy?.id,
          brandId: parentPolicy?.brandId,
          collectionName: collectionName,
        } as ProcessingPurposeInput;
      });

      setIsSubmitting(true);
      await dispatch(createPurposes(payloads));
      setIsSubmitting(false);
      dispatch(closeModal('PurposeStatusModal'));
    }
  };

  // Only reset form when grouped purpose data changes (different purpose selected)
  useEffect(() => {
    resetFormState();
  }, [groupedPurposeData]);

  useEffect(() => {
    if (visibleModal) {
      dispatch(fetchCollectionNamesIfNeeded());
      dispatch(fetchActivationEventsIfNeeded());
      dispatch(fetchGroupsIfNeeded());
    }
  }, [visibleModal]);

  return (
    <>
      <DrawerModal
        visible={visibleModal}
        onClose={() => dispatch(closeModal('PurposeStatusModal'))}
      >
        <div className="purpose-status-modal">
          <div className="status-selector-wrapper">
            <div className="purpose-modal-inputs">
              <div className="label">
                {intl.formatMessage({ id: `fleetadmin.purpose.name` })}
              </div>
              <Input
                value={selectedName}
                placeholder={intl.formatMessage({
                  id: `fleetadmin.purpose.name`,
                })}
                onChange={onChangeName}
              />
            </div>

            <div className="purpose-modal-inputs">
              <p className="label">
                {intl.formatMessage({
                  id: `fleetadmin.purpose.collectionName`,
                })}
              </p>
              <SelectorLayout
                keyPrefix="care-search"
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.purpose.collectionName',
                })}
                options={collectionNames?.map((name) => ({
                  label: intl.formatMessage({
                    id: `privacy.collectionName.${name}`,
                  }),
                  value: name,
                }))}
                value={selectedCollectionNames}
                onSelect={(value: any) => {
                  // Only allow changing collections in create mode
                  if (!groupedPurposeData) {
                    setSelectedCollectionNames(value || []);
                    setSelectedGroups([]);
                  }
                }}
                mode="multiple"
                showSelectAll={!groupedPurposeData}
                disabled={!!groupedPurposeData}
              />
            </div>

            <div className="purpose-modal-inputs">
              <p className="label">
                {intl.formatMessage({ id: `fleetadmin.purpose.startdate` })}
              </p>
              <SelectorLayout
                keyPrefix="care-search"
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.purpose.startdate',
                })}
                options={activationEvents?.map((event) => ({
                  label: intl.formatMessage({ id: `${event.key}` }),
                  value: event.key,
                }))}
                value={selectedStartDate}
                onSelect={(v) => {
                  setSelectedStartDate(v);
                  setSelectedGroups([]);
                }}
                showSelectAll
                disabled={isEditMode}
              />
            </div>

            <div className="purpose-modal-inputs">
              <div className="label">
                {intl.formatMessage({ id: `fleetadmin.purpose.minduration` })}
              </div>
              <div style={{ display: 'flex' }}>
                <Input
                  type="number"
                  value={selectedMinDurationNumber}
                  placeholder={intl.formatMessage({ id: 'number of units' })}
                  onChange={onChangeMinDurationNumber}
                  style={{ width: '50%' }}
                  min={0}
                  disabled={minDurationDisabled}
                />
                <SelectorLayout
                  keyPrefix="duration"
                  placeholder="Select unit"
                  options={durationUnits.map((unit) => ({
                    label: getLocalizedDurationLabel(unit.value),
                    value: unit.value,
                  }))}
                  value={selectedMinDurationUnit}
                  onSelect={onChangeMinDurationUnit}
                  style={{ width: '50%', marginLeft: 12 }}
                  disabled={minDurationDisabled}
                />
              </div>
            </div>

            <div className="purpose-modal-inputs">
              <div className="label">
                {intl.formatMessage({ id: `fleetadmin.purpose.maxduration` })}
              </div>
              <div style={{ display: 'flex' }}>
                <Input
                  type="number"
                  value={selectedMaxDurationNumber}
                  placeholder={intl.formatMessage({ id: 'number of units' })}
                  onChange={onChangeMaxDurationNumber}
                  style={{ width: '50%' }}
                  min={0}
                  disabled={maxDurationDisabled}
                />
                <SelectorLayout
                  keyPrefix="duration"
                  placeholder="Select unit"
                  options={durationUnits.map((unit) => ({
                    label: getLocalizedDurationLabel(unit.value),
                    value: unit.value,
                  }))}
                  value={selectedMaxDurationUnit}
                  onSelect={onChangeMaxDurationUnit}
                  style={{ width: '50%', marginLeft: 12 }}
                  disabled={maxDurationDisabled}
                />
              </div>
            </div>

            <div className="purpose-modal-inputs">
              <p className="label">
                {intl.formatMessage({ id: 'fleetadmin.purpose.groups' })}
              </p>
              <SelectorLayout
                keyPrefix="care-search"
                placeholder={intl.formatMessage({
                  id: 'fleetadmin.purpose.groups',
                })}
                options={groups
                  ?.filter(
                    (group) =>
                      group?.supportedActivationEvents?.includes(
                        selectedStartDate
                      ) &&
                      selectedCollectionNames?.includes(group?.collectionName)
                  )
                  ?.sort((a, b) =>
                    intl
                      .formatMessage({ id: `${a.key}` })
                      .localeCompare(intl.formatMessage({ id: `${b.key}` }))
                  )
                  .map((group) => ({
                    label: intl.formatMessage({ id: `${group.key}` }),
                    value: group.key,
                  }))}
                value={selectedGroups}
                mode="multiple"
                onSelect={(value: any) => setSelectedGroups(value || [])}
                showSelectAll
              />
            </div>

            {overridesApplicable && (
              <>
                <p className="period-label">
                  {intl.formatMessage({
                    id: 'fleetadmin.purpose.retentionoverrides',
                  })}
                </p>
                <Checkbox
                  style={{ marginBottom: 8 }}
                  checked={overridesEnabled}
                  onChange={onToggleOverrides}
                >
                  {intl.formatMessage({
                    id: 'fleetadmin.purpose.retentionoverrides.enable',
                  })}
                </Checkbox>

                {overridesEnabled && (
                  <div className="purpose-modal-inputs">
                    <p className="override-hint">
                      {intl.formatMessage({
                        id: 'fleetadmin.purpose.retentionoverrides.hint',
                      })}
                    </p>
                    {overrideRows.map((row, index) => (
                      <RetentionOverrideRow
                        key={row.key}
                        row={row}
                        index={index}
                        statusOptions={statusOptions}
                        blockReasonOptions={blockReasonOptions}
                        durationUnitOptions={durationUnitOptions}
                        onChange={onChangeOverrideRow}
                        onRemove={onRemoveOverrideRow}
                      />
                    ))}
                    <div className="override-add" onClick={onAddOverrideRow}>
                      <img src="/images/drawer/add.svg" alt="add" />
                      <span>
                        {intl.formatMessage({
                          id: 'fleetadmin.purpose.retentionoverrides.add',
                        })}
                      </span>
                    </div>
                    {hasDuplicateOverrides && (
                      <p className="override-warning">
                        {intl.formatMessage({
                          id: 'fleetadmin.purpose.retentionoverrides.duplicate',
                        })}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="purpose-modal-inputs">
              <p className="label">
                {intl.formatMessage({ id: 'fleetadmin.purpose.legalbasis' })}
              </p>
              <textarea
                className="comment-area"
                placeholder="Type here ..."
                value={selectedLegalBasis}
                onChange={(e) => setSelectedLegalBasis(e.target.value)}
                draggable={false}
              />
            </div>
          </div>

          <Button
            className="submit-button"
            onClick={handleOnSubmit}
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              !(
                selectedName &&
                selectedStartDate &&
                durationsValid &&
                overridesValid &&
                selectedGroups?.length > 0 &&
                selectedCollectionNames?.length > 0
              )
            }
          >
            {intl.formatMessage({ id: `submit` })}
          </Button>
          <Button
            className="cancel-button"
            onClick={handleOnCancel}
            disabled={isSubmitting}
          >
            {intl.formatMessage({ id: `cancel` })}
          </Button>
        </div>
      </DrawerModal>
      <style jsx>{styles}</style>
    </>
  );
};

export default PurposeStatusModal;
